import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { CustomTooltip } from '@AMIEWEB/Common/Tooltips';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { ITaskSearchCandidateBrand } from 'app/models/Tasks/Tasks';
import { resolveTaskSearchCandidateBrandId } from './taskSearchGridDisplayUtils';
import { OverflowTooltipWrapper } from './taskSearchGridTooltipCells';

const useBrandListStyles = makeStyles()(() => ({
  wrapper: {
    minWidth: 240,
    maxWidth: 300,
    padding: '4px 0 6px',
  },
  header: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6B7280',
    padding: '6px 14px 8px',
    borderBottom: '1px solid #E8ECF0',
    marginBottom: 4,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    margin: '0 4px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      backgroundColor: '#F0F7FC',
      boxShadow: 'inset 0 0 0 1px #D6E9F8',
    },
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#E8F4FC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: 16,
    color: '#006FB9',
  },
  brandText: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: '#1F2937',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  brandTextHover: {
    color: '#006FB9',
    textDecoration: 'underline',
  },
  chevron: {
    fontSize: 18,
    color: '#9CA3AF',
    flexShrink: 0,
  },
  chevronHover: {
    color: '#006FB9',
  },
}));

const useTriggerStyles = makeStyles()(() => ({
  multiBrandTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%',
    cursor: 'pointer',
  },
  triggerText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  brandCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    borderRadius: 9,
    backgroundColor: '#E8F4FC',
    color: '#006FB9',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
    flexShrink: 0,
  },
  arrowIcon: {
    fontSize: 16,
    color: '#006FB9',
    flexShrink: 0,
    opacity: 0.85,
  },
}));

export interface TaskSearchCandidateBrandCellProps {
  candidateId: string;
  displayText: string;
  brands?: ITaskSearchCandidateBrand[];
  fallbackBrandId?: number | null;
  dispatch: React.Dispatch<unknown>;
  theme: Theme;
  showOverflowTooltip?: boolean;
}

const openCandidateDrawer = (
  dispatch: React.Dispatch<unknown>,
  candidateId: string,
  brandId: number,
) => {
  dispatch(
    globalSearchActions.setDrawerData({
      open: true,
      data: {
        displayValue: candidateId,
        selectedorder: { row: { candidateId, brandId } },
      },
    }),
  );
};

const handleSingleBrandClick = (
  event: React.MouseEvent,
  dispatch: React.Dispatch<unknown>,
  candidateId: string,
  brandId: number,
) => {
  event.stopPropagation();
  if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
    event.preventDefault();
    openCandidateDrawer(dispatch, candidateId, brandId);
  }
};

const getLinkStyle = (theme: Theme): React.CSSProperties => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: 'inherit',
});

const BrandListRow = ({
  brand,
  candidateId,
  dispatch,
  onBrandSelect,
}: {
  brand: ITaskSearchCandidateBrand;
  candidateId: string;
  dispatch: React.Dispatch<unknown>;
  onBrandSelect?: () => void;
}) => {
  const { classes, cx } = useBrandListStyles();
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      className={classes.brandRow}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={event => {
        event.stopPropagation();
        openCandidateDrawer(dispatch, candidateId, brand.brandId);
        onBrandSelect?.(); // Close tooltip after selecting brand
      }}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          openCandidateDrawer(dispatch, candidateId, brand.brandId);
          onBrandSelect?.(); // Close tooltip after selecting brand
        }
      }}
    >
      <Box className={classes.iconWrap}>
        <BusinessOutlinedIcon className={classes.icon} />
      </Box>
      <Typography
        component="span"
        className={cx(classes.brandText, hovered && classes.brandTextHover)}
        title={brand.brand}
      >
        {brand.brand}
      </Typography>
      <ChevronRightIcon className={cx(classes.chevron, hovered && classes.chevronHover)} />
    </Box>
  );
};

const BrandListTooltipContent = ({
  brands,
  candidateId,
  dispatch,
  onBrandSelect,
}: {
  brands: ITaskSearchCandidateBrand[];
  candidateId: string;
  dispatch: React.Dispatch<unknown>;
  onBrandSelect?: () => void;
}) => {
  const { classes } = useBrandListStyles();

  return (
    <Box className={classes.wrapper}>
      <Typography component="div" className={classes.header}>
        Select brand
      </Typography>
      {brands.map(brand => (
        <BrandListRow
          key={`${brand.brandId}-${brand.brand}`}
          brand={brand}
          candidateId={candidateId}
          dispatch={dispatch}
          onBrandSelect={onBrandSelect}
        />
      ))}
    </Box>
  );
};

const MultiBrandTrigger = ({
  displayText,
  brandCount,
  theme,
}: {
  displayText: string;
  brandCount: number;
  theme: Theme;
}) => {
  const { classes } = useTriggerStyles();

  return (
    <Box
      component="span"
      className={classes.multiBrandTrigger}
      style={getLinkStyle(theme)}
      onClick={event => event.stopPropagation()}
    >
      <span className={classes.triggerText}>{displayText}</span>
      <Box className={classes.brandCount}>{brandCount}</Box>
      <KeyboardArrowDownIcon className={classes.arrowIcon} />
    </Box>
  );
};

export const TaskSearchCandidateBrandCell: React.FC<TaskSearchCandidateBrandCellProps> = ({
  candidateId,
  displayText,
  brands,
  fallbackBrandId,
  dispatch,
  theme,
  showOverflowTooltip = false,
}): React.ReactElement => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const validBrands = useMemo(
    () => (brands ?? []).filter(brand => brand.brandId != null && brand.brandId !== 0 && Boolean(brand.brand)),
    [brands],
  );

  const wrapContent = useCallback(
    (content: React.ReactElement) =>
      showOverflowTooltip ? (
        <OverflowTooltipWrapper tooltipText={displayText}>{content}</OverflowTooltipWrapper>
      ) : (
        <>{content}</>
      ),
    [displayText, showOverflowTooltip],
  );

  const handleBrandSelect = useCallback(() => {
    setTooltipOpen(false); // Close tooltip when brand is selected
  }, []);

  if (validBrands.length > 1) {
    return wrapContent(
      <div
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <CustomTooltip
          open={tooltipOpen}
          tooltipPlacementBottom
          standardMargin
          disableMaxWidth
          disableInteractive={false}
          placement="bottom-start"
          tooltipContent={
            <BrandListTooltipContent
              brands={validBrands}
              candidateId={candidateId}
              dispatch={dispatch}
              onBrandSelect={handleBrandSelect}
            />
          }
          renderedFromGrid
          isInlineContent
        >
          <MultiBrandTrigger displayText={displayText} brandCount={validBrands.length} theme={theme} />
        </CustomTooltip>
      </div>,
    );
  }

  const brandId =
    validBrands.length === 1
      ? validBrands[0].brandId
      : resolveTaskSearchCandidateBrandId(validBrands, fallbackBrandId);

  if (brandId == null) {
    return wrapContent(<Box>{displayText}</Box>);
  }

  const to = `/candidate/${candidateId}/${brandId}`;

  return wrapContent(
    <RouterLink
      to={to}
      style={getLinkStyle(theme)}
      onClick={event => handleSingleBrandClick(event, dispatch, candidateId, brandId)}
    >
      {displayText}
    </RouterLink>,
  );
};